import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './awsConfig';
import { AuthProvider } from './AuthContext';
import Home from './pages/Home';
import RestaurantList from './pages/RestaurantList';
import GuestMenu from './pages/GuestMenu';
import DriverService from './pages/DriverService';
import Photography from './pages/Photography';
import KitchenPanel from './pages/KitchenPanel';
import DeliveryPanel from './pages/DeliveryPanel';
import AdminPanel from './pages/AdminPanel';
import AdminLogin from './pages/AdminLogin';
import StaffLogin from './pages/StaffLogin';
import KitchenPairing from './pages/KitchenPairing';
import StaffDashboard from './pages/StaffDashboard';
import QRGenerator from './pages/QRGenerator';
import LandingPage from './pages/LandingPage';
import PartnerLogin from './pages/PartnerLogin';
import PartnerDashboard from './pages/PartnerDashboard';
import PartnerWallet from './pages/PartnerWallet';
import ProtectedPartnerRoute from './components/ProtectedPartnerRoute';
import AuthCallback from './components/AuthCallback';
import ErrorBoundary from './components/ErrorBoundary';
import { isStaffAuthed, isAdminAuthed } from './menuApi';

// The partner portal's backend routes are not deployed yet. Keep its pages out
// of the live bundle's router until they are, so nobody reaches a login form
// that cannot succeed.
const PARTNER_PORTAL_ENABLED = process.env.REACT_APP_PARTNER_PORTAL === 'on';

// "/" is where Cognito sends guests back after Google sign-in. If a return is
// in progress, show the sign-in interstitial instead of the marketing page so
// guests are handed straight back to their order without a landing-page flash.
function RootRoute() {
  const returnPath = localStorage.getItem('oauth_return_path');
  // A "?code=" on "/" means Cognito just handed the guest back from Google — always
  // show the sign-in interstitial then, even if oauth_return_path was lost (e.g. the
  // guest returned in a different browser context). Otherwise we'd flash the
  // marketing page / a blank screen instead of resuming their order.
  const hasAuthCode = new URLSearchParams(window.location.search).has('code');
  const returningFromOAuth = hasAuthCode || (returnPath && returnPath !== window.location.pathname);
  return returningFromOAuth ? <AuthCallback /> : <LandingPage />;
}

// Gate on a signed, unexpired token carrying role=admin — not on a localStorage
// boolean. The old `adminAuth === 'true'` check was settable from devtools, and
// because the admin panel's data calls authenticate with the STAFF token, any
// kitchen worker holding an access code could promote themselves to super admin
// with one line in the console.
function ProtectedAdminRoute({ children }) {
  return isAdminAuthed() ? children : <Navigate to="/admin-login" />;
}

// Gate on a live server-issued token, not a localStorage boolean anyone can set.
// The token expires, so an unattended kitchen tablet does not stay authorised
// forever.
function ProtectedStaffRoute({ children }) {
  return isStaffAuthed() ? children : <Navigate to="/staff-login" />;
}

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
    <Router>
      <Routes>
        <Route path="/" element={<RootRoute />} />
        <Route path="/:hotelId/:roomNumber" element={<Home />} />
        <Route path="/food/:hotelId/:roomNumber" element={<RestaurantList />} />
        <Route path="/food/:hotelId/:roomNumber/:restaurantId" element={<GuestMenu />} />
        <Route path="/driver/:hotelId/:roomNumber" element={<DriverService />} />
        <Route path="/photography/:hotelId/:roomNumber" element={<Photography />} />
        {/* Both panels show live orders — guest names, room numbers, order
            contents — and can accept/reject them. They were unguarded. */}
        <Route path="/kitchen" element={
          <ProtectedStaffRoute>
            <KitchenPanel />
          </ProtectedStaffRoute>
        } />
        <Route path="/delivery" element={
          <ProtectedStaffRoute>
            <DeliveryPanel />
          </ProtectedStaffRoute>
        } />
        <Route path="/admin-login" element={<AdminLogin />} />
        <Route path="/admin" element={
          <ProtectedAdminRoute>
            <AdminPanel />
          </ProtectedAdminRoute>
        } />
        <Route path="/staff-login" element={<StaffLogin />} />
        {/* Restaurant partner portal. Static segments outrank the /:hotelId/:roomNumber
            guest route in React Router v6, so these resolve correctly.

            Off unless REACT_APP_PARTNER_PORTAL=on. The backend these pages call
            (/partner/* on z51smv2b7g) does not exist yet, so shipping them live
            would put a working-looking credential form in front of restaurant
            owners that can only ever fail. The flag is the switch that turns the
            portal on the day its backend is deployed — not a reason to delete
            the pages. */}
        {PARTNER_PORTAL_ENABLED && (
          <>
            <Route path="/partner/login" element={<PartnerLogin />} />
            <Route path="/partner" element={
              <ProtectedPartnerRoute>
                <PartnerDashboard />
              </ProtectedPartnerRoute>
            } />
            <Route path="/partner/wallet" element={
              <ProtectedPartnerRoute>
                <PartnerWallet />
              </ProtectedPartnerRoute>
            } />
          </>
        )}
        <Route path="/qr-generator" element={<QRGenerator />} />
        <Route path="/staff" element={
          <ProtectedStaffRoute>
            <StaffDashboard />
          </ProtectedStaffRoute>
        } />
        {/* A restaurant's own front door: gapgo.redspeed.com.au/darcys.
            Declared LAST so every static route above outranks it — React Router v6
            ranks static segments over dynamic ones regardless of order, but keeping
            it here makes that explicit to the next reader.
            KitchenPairing resolves the slug against the API and shows a plain
            "we don't recognise that address" if it is not a restaurant, which is
            why this can safely sit in front of the catch-all: an unknown single
            segment now gets an explanation instead of a silent bounce to
            marketing that reads as "GapGo is broken". */}
        <Route path="/:restaurantSlug" element={<KitchenPairing />} />
        {/* Any unrecognised path falls back to root instead of a blank screen. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;